import { FC } from 'react';
import { Button, Dropdown, InformedField, Modal, TextField } from '@components';
import { Form, FormState } from 'informed';
import { useRepository } from '@context';
import { useMutation, useQuery } from 'react-query';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import { IDropdownOption, IUsersOptions, Nullable, UserCreationPayload, UserDefault } from '@common/interfaces';
import css from './styles.module.scss';

interface IUserModal {
  closeModal: () => void;
  userData: Nullable<UserDefault>;
}

interface UserFormValues {
  admin_permissions: Nullable<IDropdownOption>;
  customer_area: Nullable<IDropdownOption>;
  email: string;
  facility_permissions: IDropdownOption[];
  first_name: string;
  last_name: string;
}

enum AdminPermissions {
  DO_SUPERUSER = 'do_superuser',
  NO_PERMISSIONS = 'no_permissions',
}

const permissionOptions = [
  { label: 'Digital Office Superuser', value: AdminPermissions.DO_SUPERUSER },
  { label: 'No permissions', value: AdminPermissions.NO_PERMISSIONS },
];

const UserModal: FC<IUserModal> = ({ closeModal, userData }) => {
  const isNew = !userData;

  console.log('userData', userData);

  const { accountRepository } = useRepository();

  const { data: options, isLoading: isOptionsLoading } = useQuery<IUsersOptions>('user-options', () =>
    accountRepository.getUserOptions()
  );

  const { mutate: createUser, isLoading: inCreationUserProgress } = useMutation(
    (data: UserCreationPayload) => accountRepository.createUser(data),
    {
      onSuccess: () => {
        toast.success('User creation request was sent');
        closeModal();
      },
    }
  );

  const onSubmit = ({ values }: FormState) => {
    const { admin_permissions, customer_area, email, facility_permissions, first_name, last_name } =
      values as unknown as UserFormValues;

    const formData = {
      user_location: customer_area?.value,
      email,
      facility_permissions: facility_permissions.map(({ value }) => ({ id: parseFloat(value), do_manager: true })),
      first_name,
      last_name,
      do_superuser: admin_permissions?.value === AdminPermissions.DO_SUPERUSER,
    };

    if (isNew) {
      createUser(formData);
    } else {
      // TO DO: Waiting API for edit
    }
  };

  const yupSchema = Yup.object().shape({
    admin_permissions: Yup.object(),
    customer_area: Yup.object(),
    email: Yup.string().when([], {
      is: () => isNew === true,
      then: (schema) => schema.required('Required'),
      otherwise: (schema) => schema.notRequired(),
    }),
    facility_permissions: Yup.array().of(
      Yup.object({
        label: Yup.string().required(),
        value: Yup.string().required(),
      })
    ),
    first_name: Yup.string(),
    last_name: Yup.string(),
  });

  const currentUserFacilities = userData?.facilities.map(({ facility_id_on_hbh }) => String(facility_id_on_hbh)) || [];

  const initialValues = {
    admin_permissions: null, // TO DO - BE
    customer_area: null, // TO DO - BE
    email: userData ? userData.email : '',
    facility_permissions: options?.facilities.filter(({ value }) => currentUserFacilities.includes(value)) || [],
    first_name: userData ? userData.first_name : '',
    last_name: userData ? userData.last_name : '',
  };

  const loading = isOptionsLoading || inCreationUserProgress;

  return (
    <Modal
      variant='dark'
      className={css.modal}
      title={isNew ? 'Create new user' : 'Edit user'}
      closeModal={closeModal}
      loading={loading}
    >
      {options ? (
        <Form onSubmit={onSubmit} yupSchema={yupSchema} initialValues={initialValues}>
          <Modal.Content>
            <div className={css.form}>
              {userData ? (
                <div className={css.info}>
                  <div className={css.row}>
                    <div className={css.label}>User email address</div>
                    <div className={css.value}>{userData.email}</div>
                  </div>
                  <div className={css.row}>
                    <div className={css.label}>Organisation</div>
                    <div className={css.value}>{userData.organisation?.short_name || '-'}</div>
                  </div>
                </div>
              ) : null}
              <InformedField label='First name'>
                <TextField placeholder='Enter first name' variant='dark' name='first_name' autoComplete='off' />
              </InformedField>
              <InformedField label='Last name'>
                <TextField placeholder='Enter last name' variant='dark' name='last_name' autoComplete='off' />
              </InformedField>
              {isNew ? (
                <InformedField label='Email address *'>
                  <TextField placeholder='Enter email address' variant='dark' name='email' autoComplete='off' />
                </InformedField>
              ) : null}
              <InformedField label='Digital Office'>
                <Dropdown
                  placeholder='Select one or multiple options'
                  name='facility_permissions'
                  options={options.facilities}
                  variant='dark'
                  isMulti
                />
              </InformedField>
              <InformedField label='Customer Area'>
                <Dropdown
                  placeholder='Select customer area'
                  name='customer_area'
                  options={options.customer_areas}
                  variant='dark'
                />
              </InformedField>
              <InformedField label='Admin permission'>
                <Dropdown
                  placeholder='Select admin permissions'
                  name='admin_permissions'
                  options={permissionOptions}
                  variant='dark'
                />
              </InformedField>
            </div>
          </Modal.Content>
          <Modal.Footer>
            <Button text='Cancel' variant='transparent' onClick={closeModal} />
            {isNew ? (
              <Button text='Confirm and create user' type='submit' />
            ) : (
              <Button text='Confirm and update user' variant='forest' type='submit' />
            )}
          </Modal.Footer>
        </Form>
      ) : null}
    </Modal>
  );
};

export default UserModal;
